import { StudioArt, StudioPin, StudioRoomState } from '../types'
import { printHeight, wallGeometry } from '../babylon/sceneMath'

/** ~1.5 m of print center — same heuristic as the studio lightbox filter. */
export const PIN_ART_BIND_RADIUS_SQ = 2.25

/** Horizontal gap past the frame edge (meters). */
const STICKY_EDGE_MARGIN = 0.22
/** Push off the wall so the pin sits in front of the frame. */
const STICKY_NORMAL_OUT = 0.08
/** Vertical spacing between stacked stickies on one print. */
const STICKY_STACK_STEP = 0.32

export function artWorldPosition(
  art: Pick<StudioArt, 'side' | 'u' | 'y'>,
  room: Pick<StudioRoomState, 'width' | 'depth'>,
) {
  return wallGeometry(art.side, art.u, art.y, room).position
}

/** Nearest non-placeholder print within bind radius, or undefined for a wall-only pin. */
export function nearestArtIdForPin(
  position: [number, number, number],
  arts: StudioArt[],
  room: Pick<StudioRoomState, 'width' | 'depth'>,
): string | undefined {
  let bestId: string | undefined
  let bestDist = PIN_ART_BIND_RADIUS_SQ
  for (const art of arts) {
    if (art.isPlaceholder) continue
    const p = artWorldPosition(art, room)
    const dx = position[0] - p.x
    const dy = position[1] - p.y
    const dz = position[2] - p.z
    const d = dx * dx + dy * dy + dz * dz
    if (d < bestDist) {
      bestDist = d
      bestId = art.id
    }
  }
  return bestId
}

/** Pins bound to a print by artId, oldest → newest (stable stack order). */
export function pinsBoundToArt(artId: string, pins: StudioPin[]): StudioPin[] {
  return pins
    .filter((p) => p.artId === artId)
    .sort((a, b) => (a.updatedAt ?? 0) - (b.updatedAt ?? 0) || a.id.localeCompare(b.id))
}

export function stickySlotIndex(pin: StudioPin, pins: StudioPin[]): number {
  if (!pin.artId) return 0
  const bound = pinsBoundToArt(pin.artId, pins)
  const idx = bound.findIndex((p) => p.id === pin.id)
  return idx >= 0 ? idx : bound.length
}

/**
 * Museum-label position just outside the frame (viewer's right), stacked
 * downward for multiple notes on the same print.
 */
export function stickyPinWorldTuple(
  art: Pick<StudioArt, 'side' | 'u' | 'y' | 'w' | 'ratio'>,
  room: Pick<StudioRoomState, 'width' | 'depth'>,
  slotIndex: number,
): [number, number, number] {
  const ph = printHeight(art)
  const { position, normal } = wallGeometry(art.side, art.u, art.y, room)
  const along = art.w / 2 + STICKY_EDGE_MARGIN
  const y = position.y + ph / 2 - 0.12 - slotIndex * STICKY_STACK_STEP

  // Viewer's right when facing the print from inside the room.
  if (art.side === -1) {
    return [position.x + normal.x * STICKY_NORMAL_OUT, y, position.z - along]
  }
  if (art.side === 1) {
    return [position.x + normal.x * STICKY_NORMAL_OUT, y, position.z + along]
  }
  return [position.x - along, y, position.z + normal.z * STICKY_NORMAL_OUT]
}

/** World position for overlay — sticky exterior when bound to a print, else stored xyz. */
export function resolvePinWorldTuple(
  pin: StudioPin,
  arts: StudioArt[],
  pins: StudioPin[],
  room: Pick<StudioRoomState, 'width' | 'depth'>,
): [number, number, number] {
  if (pin.artId) {
    const art = arts.find((a) => a.id === pin.artId && !a.isPlaceholder)
    if (art) {
      return stickyPinWorldTuple(art, room, stickySlotIndex(pin, pins))
    }
  }
  return pin.position
}

/** Pins for a lightbox print — prefer artId, fall back to proximity for legacy saves. */
export function pinsForArt(
  art: StudioArt,
  pins: StudioPin[],
  room: Pick<StudioRoomState, 'width' | 'depth'>,
): StudioPin[] {
  const byId = pinsBoundToArt(art.id, pins)
  if (byId.length > 0) return byId
  const center = artWorldPosition(art, room)
  return pins.filter((p) => {
    if (p.artId) return false
    const dx = p.position[0] - center.x
    const dy = p.position[1] - center.y
    const dz = p.position[2] - center.z
    return dx * dx + dy * dy + dz * dz < PIN_ART_BIND_RADIUS_SQ
  })
}

export function pinCountForArt(artId: string, pins: StudioPin[]): number {
  return pins.filter((p) => p.artId === artId).length
}
